import { useEffect, useState, type ComponentType, type FormEvent, type SVGProps } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  IconBillCheck,
  IconCalendar,
  IconChart,
  IconCheck,
  IconClock,
  IconClose,
  IconEdit,
  IconIdCard,
  IconOtLamp,
  IconPrinter,
  IconPulse,
  IconRefreshCw,
  IconWallet,
} from '../../components/layout/icons';
import { BillActionGrid, BillActionTile } from '../../components/ui/BillActionTile';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { FieldError } from '../../components/ui/FieldError';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { confirmAction, confirmUpdate } from '../../lib/confirm-service';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import type { BillableAdmission } from '../ipd-daycare/billable-admission';
import { IpdAllBillList } from '../ipd-daycare/IpdAllBillList';
import { IpdAllReceiptList } from '../ipd-daycare/IpdAllReceiptList';
import { IpdAllRefundList } from '../ipd-daycare/IpdAllRefundList';
import { IpdBillDetailsView } from '../ipd-daycare/IpdBillDetailsView';
import { IpdNewReceiptForm } from '../ipd-daycare/IpdNewReceiptForm';
import { useAddIpdBillRefund, useIpdBills } from '../ipd-daycare/ipd-daycare.queries';
import { currency, formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import type { IpdBill, IpdBillPaymentMode } from '../ipd-daycare/types';
import { OtCreateBillForm } from './OtCreateBillForm';
import { OtEditBillForm } from './OtEditBillForm';
import { useOtCase, useOtLookup, useScheduleOtCase, useUpdateOtCaseStatus } from './ot.queries';
import { FieldLabel, LookupSelect, PatientDemographicFields, PatientIdentityFields, ResponsiblePersonFields } from './ot.shared';
import { formatOtDate, formatOtDateTime, formatOtTime, statusLabels, toDateInput, todayInput } from './ot.utils';
import type { OtCase, OtCaseStatus, OtSchedulePayload, PatientFormFields, ResponsiblePersonFormFields } from './types';

type BillTab = 'bill-details' | 'create-bill' | 'edit-bill' | 'new-receipt' | 'refund' | 'all-bill' | 'all-receipt' | 'all-refund';
type DetailTab = 'case-details' | 'schedule' | BillTab;

// Same billing tiles as Dialysis; bills run on the shared IPD billing engine.
const billTiles: Array<{ key: BillTab; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }> = [
  { key: 'bill-details', label: 'Bill Details', icon: IconWallet },
  { key: 'create-bill', label: 'Create New Bill', icon: IconPulse },
  { key: 'edit-bill', label: 'Edit Bill', icon: IconEdit },
  { key: 'new-receipt', label: 'New Receipt', icon: IconClock },
  { key: 'refund', label: 'Refund', icon: IconRefreshCw },
  { key: 'all-bill', label: 'All Bill', icon: IconChart },
  { key: 'all-receipt', label: 'All Receipt', icon: IconChart },
  { key: 'all-refund', label: 'All Refund', icon: IconRefreshCw },
];

// Billing components share the admission status vocabulary; a completed OT case reads as discharged.
const billableStatus = (status: OtCaseStatus): BillableAdmission['status'] => {
  if (status === 'cancelled') return 'cancelled';
  return status === 'completed' ? 'discharged' : 'admitted';
};

const refundPaymentModes: IpdBillPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

const OtRefundPanel = ({ patientName, bill, bills }: { patientName: string; bill: IpdBill; bills: IpdBill[] }) => {
  const [mode, setMode] = useState<IpdBillPaymentMode>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const [showAmountError, setShowAmountError] = useState(false);
  const mutation = useAddIpdBillRefund();
  const { showToast } = useToast();
  const refunds = bills.flatMap((item) => item.refunds.map((refund) => ({ bill: item, refund })))
    .sort((left, right) => new Date(right.refund.date).getTime() - new Date(left.refund.date).getTime());
  const totalRefunded = refunds.reduce((sum, item) => sum + item.refund.amount, 0);

  const submit = async () => {
    setShowAmountError(true);
    if (amount <= 0 || amount > bill.creditAmount) return;
    const confirmed = await confirmAction({
      title: 'Confirm Refund',
      description: `Are you sure you want to record a refund of ${currency(amount)} for this OT bill?`,
      confirmLabel: 'Yes, Record Refund',
      tone: 'danger',
    });
    if (!confirmed) return;
    mutation.mutate({ billId: bill.id, amount, mode, bankName: bankName.trim() || null }, {
      onSuccess: () => {
        setAmount(0);
        setBankName('');
        setShowAmountError(false);
        showToast('Refund saved successfully.', 'success');
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The refund could not be recorded.', 'error'),
    });
  };

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>REFUND - {patientName.toUpperCase()}</span></div>
        <p>Bill #{bill.billNumber} | Bill Date : {formatOtDate(bill.billDate)} | Credit {currency(bill.creditAmount)}</p>
      </header>
      <section className="opd-bill-subsection">
        <h3>Record a refund</h3>
        <div className="ipd-daycare-admission-row">
          <label><span className="ipd-daycare-label-text">Bill</span><input value={`Bill #${bill.billNumber} - Credit ${currency(bill.creditAmount)}`} disabled /></label>
          <label><span className="ipd-daycare-label-text">Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as IpdBillPaymentMode)}>{refundPaymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span className="ipd-daycare-label-text">Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
          <label><span className="ipd-daycare-label-text">Amount</span><input type="number" min={0} max={bill.creditAmount} step="0.01" value={amount} aria-invalid={showAmountError && (amount <= 0 || amount > bill.creditAmount)} onChange={(event) => setAmount(Number(event.target.value))} /><FieldError message={showAmountError && amount <= 0 ? 'Amount must be greater than zero.' : showAmountError && amount > bill.creditAmount ? `Amount cannot exceed ${currency(bill.creditAmount)}.` : undefined} /></label>
        </div>
        <div className="opd-form-actions ot-refund-actions"><button className="button button-primary" type="button" disabled={mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? 'Saving...' : 'Refund'}</button></div>
      </section>
      <section className="opd-bill-subsection">
        <div className="opd-bill-subsection-head"><h3>Refunds across OT bills</h3><span>Total Refunded <strong>{currency(totalRefunded)}</strong></span></div>
        {refunds.length === 0 ? (
          <p className="opd-bill-empty-note">No refunds have been recorded for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><th>Bill No</th><th>Date</th><th>Mode</th><th>Bank</th><th>Refund By</th><th>Amount</th></tr></thead>
              <tbody>{refunds.map((item, index) => <tr key={`${item.bill.id}-${item.refund.id}`}><td>{index + 1}</td><td>{item.bill.billNumber}</td><td>{formatIpdDateTime(item.refund.date)}</td><td>{item.refund.mode ?? '-'}</td><td>{item.refund.bankName ?? '-'}</td><td>{item.refund.createdBy.name}</td><td>{currency(item.refund.amount)}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

const statusActions: Record<OtCaseStatus, { status: OtCaseStatus; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[]> = {
  registered: [{ status: 'cancelled', label: 'Cancel Case', icon: IconClose }],
  scheduled: [{ status: 'in-progress', label: 'Start Case', icon: IconOtLamp }, { status: 'cancelled', label: 'Cancel Case', icon: IconClose }],
  'in-progress': [{ status: 'completed', label: 'Mark Completed', icon: IconCheck }, { status: 'cancelled', label: 'Cancel Case', icon: IconClose }],
  completed: [],
  cancelled: [],
};

const validateSchedule = (values: OtSchedulePayload) => {
  const errors: Record<string, string> = {};
  if (!values.registrationDate) errors.registrationDate = 'Scheduled Date is required.';
  if (!values.insuranceType) errors.insuranceType = 'Patient Type is required.';
  if (!values.name.trim()) errors.name = "Patient's name is required.";
  if ((values.phone ?? '').trim() && !/^\d{10}$/.test((values.phone ?? '').trim())) errors.phone = 'Enter a 10 digit phone number.';
  if ((values.guardianContactNo ?? '').trim() && !/^\d{10}$/.test((values.guardianContactNo ?? '').trim())) errors.guardianContactNo = 'Enter a 10 digit phone number.';
  if (!(values.guardianName ?? '').trim()) errors.guardianName = 'Guardian Name is required.';
  if (!values.relation) errors.relation = 'Relation is required.';
  if (!(values.address ?? '').trim()) errors.address = 'Address is required.';
  if (!(values.state ?? '').trim()) errors.state = 'State is required.';
  if (!values.underDoctor) errors.underDoctor = 'Under Doctor is required.';
  if (!values.bedCategory) errors.bedCategory = 'Bed Category is required.';
  if (!values.bed) errors.bed = 'Bed is required.';
  if (!values.referredBy) errors.referredBy = 'Referred By is required.';
  if (values.responsiblePerson.phone.trim() && !/^\d{10}$/.test(values.responsiblePerson.phone.trim())) errors.responsiblePersonPhone = 'Enter a 10 digit phone number.';
  if (!values.procedureType) errors.procedureType = 'Procedure Type is required.';
  if (!values.otRoom) errors.otRoom = 'OT Room is required.';
  if (!values.surgeon) errors.surgeon = 'Surgeon is required.';
  if (!values.anesthetist) errors.anesthetist = 'Anesthetist is required.';
  if (!values.scheduledDate) errors.scheduledDate = 'OT Date is required.';
  if (!values.startTime) errors.startTime = 'Start Time is required.';
  if (!values.endTime) errors.endTime = 'End Time is required.';
  else if (values.startTime && values.endTime <= values.startTime) errors.endTime = 'End Time must be after Start Time.';
  return errors;
};

const scheduleValuesFrom = (row: OtCase): OtSchedulePayload => ({
  patientId: row.patient.id,
  uhid: row.patientSnapshot.uhid,
  name: row.patientSnapshot.name,
  phone: row.patientSnapshot.phone ? normalizePhoneInput(row.patientSnapshot.phone) : null,
  gender: row.patientSnapshot.gender,
  maritalStatus: row.patientSnapshot.maritalStatus,
  guardianName: row.patientSnapshot.guardianName,
  relation: row.patientSnapshot.relation,
  guardianContactNo: row.patientSnapshot.guardianContactNo ? normalizePhoneInput(row.patientSnapshot.guardianContactNo) : null,
  dateOfBirth: row.patientSnapshot.dateOfBirth,
  ageYears: row.patientSnapshot.ageYears,
  ageMonths: row.patientSnapshot.ageMonths,
  ageDays: row.patientSnapshot.ageDays,
  address: row.patientSnapshot.address,
  state: row.patientSnapshot.state,
  district: row.patientSnapshot.district,
  pinCode: row.patientSnapshot.pinCode,
  aadhaar: row.patientSnapshot.aadhaar,
  registrationDate: toDateInput(row.registrationDate),
  insuranceType: row.insuranceType ?? '',
  insuranceNumber: row.insuranceNumber,
  underDoctor: row.underDoctor?.id ?? null,
  bedCategory: row.bedCategory?.id ?? null,
  bed: row.bed?.id ?? null,
  referredBy: row.referredBy?.id ?? null,
  responsiblePerson: {
    name: row.responsiblePerson.name ?? '',
    relation: row.responsiblePerson.relation ?? '',
    phone: row.responsiblePerson.phone ? normalizePhoneInput(row.responsiblePerson.phone) : '',
    age: row.responsiblePerson.age,
    address: row.responsiblePerson.address ?? '',
  },
  diagnosis: row.diagnosis,
  completedRequiredTests: (row.completedRequiredTests ?? []).map((test) => test.id),
  procedureType: row.procedureType?.id ?? '',
  otRoom: row.otRoom?.id ?? '',
  // Default the surgeon to the registering doctor until a surgeon has been chosen.
  surgeon: row.surgeon?.id ?? row.underDoctor?.id ?? '',
  anesthetist: row.anesthetist?.id ?? '',
  scheduledDate: row.scheduledDate ? toDateInput(row.scheduledDate) : todayInput(),
  startTime: row.startTime ?? '09:00',
  endTime: row.endTime ?? '10:00',
});

const OtScheduleForm = ({ row, onSaved }: { row: OtCase; onSaved: () => void }) => {
  const { showToast } = useToast();
  const mutation = useScheduleOtCase();
  const procedureTypes = useOtLookup('procedure-types');
  const doctors = useOtLookup('doctors');
  const wards = useOtLookup('wards');
  const referrals = useOtLookup('referrals');
  const tpas = useOtLookup('tpas');
  const [values, setValues] = useState(() => scheduleValuesFrom(row));
  const otRooms = useOtLookup('ot-rooms', '', { scheduledDate: values.scheduledDate, startTime: values.startTime, endTime: values.endTime, excludeId: row.id });
  const states = useOtLookup('states');
  const districts = useOtLookup('districts', '', { state: values.state ?? '' });
  const beds = useOtLookup('beds', '', { ward: values.bedCategory ?? '', scheduledDate: values.scheduledDate, startTime: values.startTime, endTime: values.endTime, excludeId: row.id });
  const [showErrors, setShowErrors] = useState(false);
  const [roomAvailabilityError, setRoomAvailabilityError] = useState<string | undefined>();
  const errors = showErrors ? validateSchedule(values) : {};
  const set = <K extends keyof OtSchedulePayload>(key: K, value: OtSchedulePayload[K]) => setValues((current) => ({ ...current, [key]: value }));
  const setPatient = (patient: PatientFormFields) => setValues((current) => ({ ...current, ...patient }));
  const setResponsiblePerson = (responsiblePerson: ResponsiblePersonFormFields) => setValues((current) => ({ ...current, responsiblePerson }));
  const isFirstSchedule = row.status === 'registered';
  const selectedProcedure = procedureTypes.data?.find((procedure) => procedure.id === values.procedureType);
  const requiredTests = selectedProcedure?.requiredTests ?? [];
  useEffect(() => { setRoomAvailabilityError(undefined); }, [values.scheduledDate, values.startTime, values.endTime]);
  const patientTypeOptions = (tpas.data ?? []).some((item) => item.label === values.insuranceType) || !values.insuranceType
    ? tpas.data ?? []
    : [{ id: `current-${values.insuranceType}`, label: values.insuranceType }, ...(tpas.data ?? [])];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (Object.keys(validateSchedule(values)).length > 0) return;
    const selectedRoom = otRooms.data?.find((room) => room.id === values.otRoom);
    if (selectedRoom?.available === false) {
      const message = 'The selected OT room is already occupied for this date and time.';
      setRoomAvailabilityError(message);
      showToast(message, 'warning');
      return;
    }
    mutation.mutate({ id: row.id, payload: values }, {
      onSuccess: () => { showToast(isFirstSchedule ? 'OT case scheduled successfully.' : 'OT schedule updated successfully.', 'success'); onSaved(); },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The OT case could not be scheduled.', 'error'),
    });
  };

  return (
    <form className="ipd-daycare-registration-card ot-edit-details-form" noValidate onSubmit={submit}>
      <section className="ipd-daycare-section"><h2>Case Information</h2>
        <div className="dialysis-top-row">
          <label><FieldLabel required>Scheduled Date</FieldLabel><input type="date" value={values.registrationDate} onChange={(event) => set('registrationDate', event.target.value)} required aria-invalid={Boolean(errors.registrationDate)} /><FieldError message={errors.registrationDate} /></label>
          <label><FieldLabel required>Patient Type</FieldLabel><select value={values.insuranceType} onChange={(event) => set('insuranceType', event.target.value)} required aria-invalid={Boolean(errors.insuranceType)}><option value="">Select</option>{patientTypeOptions.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}</select><FieldError message={errors.insuranceType} /></label>
          <label><FieldLabel>Insurance No</FieldLabel><input value={values.insuranceNumber ?? ''} onChange={(event) => set('insuranceNumber', event.target.value || null)} maxLength={100} /></label>
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Patient Details</h2>
        <div className="ipd-daycare-patient-row1">
          <label><FieldLabel>UHID</FieldLabel><input value={values.uhid ?? ''} readOnly /></label>
          <PatientIdentityFields value={values} onChange={setPatient} errors={errors} />
        </div>
        <div className="ipd-daycare-patient-row2">
          <PatientDemographicFields value={values} onChange={setPatient} states={states.data} districts={districts.data} errors={errors} />
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Patient Admission Information</h2>
        <div className="ipd-daycare-admission-row ot-admission-row">
          <label><FieldLabel required>Under Doctor</FieldLabel><LookupSelect id="ot-schedule-under-doctor" value={values.underDoctor ?? ''} options={doctors.data} placeholder="Select Doctor" ariaInvalid={Boolean(errors.underDoctor)} onChange={(value) => set('underDoctor', value || null)} /><FieldError message={errors.underDoctor} /></label>
          <label><FieldLabel>Department</FieldLabel><input value={doctors.data?.find((doctor) => doctor.id === values.underDoctor)?.department ?? row.underDoctor?.department ?? ''} placeholder="Select Under Doctor" readOnly /></label>
          <label><FieldLabel required>Bed Category</FieldLabel><LookupSelect id="ot-schedule-bed-category" value={values.bedCategory ?? ''} options={wards.data} placeholder="Select Ward" clearable={false} ariaInvalid={Boolean(errors.bedCategory)} onChange={(value) => setValues((current) => ({ ...current, bedCategory: value || null, bed: null }))} /><FieldError message={errors.bedCategory} /></label>
          <label><FieldLabel required>Bed</FieldLabel><LookupSelect id="ot-schedule-bed" value={values.bed ?? ''} options={beds.data} placeholder={values.bedCategory ? 'Select Bed' : 'Select Bed Category first'} disabled={!values.bedCategory} ariaInvalid={Boolean(errors.bed)} onChange={(value) => set('bed', value || null)} /><FieldError message={errors.bed} /></label>
          <label><FieldLabel required>Referred By</FieldLabel><LookupSelect id="ot-schedule-referred-by" value={values.referredBy ?? ''} options={referrals.data} placeholder="Select Referral" ariaInvalid={Boolean(errors.referredBy)} onChange={(value) => set('referredBy', value || null)} /><FieldError message={errors.referredBy} /></label>
          <label className="ipd-daycare-wide"><CkEditorField label="Diagnosis" value={values.diagnosis ?? ''} onChange={(value) => set('diagnosis', value || null)} /></label>
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Responsible Person Details</h2>
        <div className="ipd-daycare-responsible-row">
          <ResponsiblePersonFields value={values.responsiblePerson} onChange={setResponsiblePerson} errors={errors} />
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>{isFirstSchedule ? 'Schedule Operation' : 'Reschedule Operation'}</h2>
        <div className="ipd-daycare-admission-row">
          <label><FieldLabel required>Procedure Type</FieldLabel><LookupSelect id="ot-procedure-type" value={values.procedureType} options={procedureTypes.data} placeholder="Select Procedure" clearable={false} ariaInvalid={Boolean(errors.procedureType)} onChange={(value) => setValues((current) => value === current.procedureType ? current : { ...current, procedureType: value, completedRequiredTests: [] })} /><FieldError message={errors.procedureType} /></label>
          <label><FieldLabel required>OT Room</FieldLabel><LookupSelect id="ot-room" value={values.otRoom} options={otRooms.data} placeholder="Select OT Room" clearable={false} ariaInvalid={Boolean(errors.otRoom || roomAvailabilityError)} onChange={(value) => { const room = otRooms.data?.find((option) => option.id === value); if (room?.available === false) { const message = 'The selected OT room is already occupied for this date and time.'; setRoomAvailabilityError(message); showToast(message, 'warning'); return; } setRoomAvailabilityError(undefined); set('otRoom', value); }} /><FieldError message={roomAvailabilityError || errors.otRoom} /></label>
          <label><FieldLabel required>Surgeon</FieldLabel><LookupSelect id="ot-surgeon" value={values.surgeon} options={doctors.data} placeholder="Select Surgeon" ariaInvalid={Boolean(errors.surgeon)} onChange={(value) => set('surgeon', value)} /><FieldError message={errors.surgeon} /></label>
          <label><FieldLabel required>Anesthetist</FieldLabel><LookupSelect id="ot-anesthetist" value={values.anesthetist} options={doctors.data} placeholder="Select Anesthetist" ariaInvalid={Boolean(errors.anesthetist)} onChange={(value) => set('anesthetist', value)} /><FieldError message={errors.anesthetist} /></label>
          <label><FieldLabel required>OT Date</FieldLabel><input type="date" value={values.scheduledDate} onChange={(event) => set('scheduledDate', event.target.value)} required aria-invalid={Boolean(errors.scheduledDate)} /><FieldError message={errors.scheduledDate} /></label>
          <label><FieldLabel required>Start Time</FieldLabel><input type="time" value={values.startTime} onChange={(event) => set('startTime', event.target.value)} required aria-invalid={Boolean(errors.startTime)} /><FieldError message={errors.startTime} /></label>
          <label><FieldLabel required>End Time</FieldLabel><input type="time" value={values.endTime} onChange={(event) => set('endTime', event.target.value)} required aria-invalid={Boolean(errors.endTime)} /><FieldError message={errors.endTime} /></label>
        </div>
        {requiredTests.length > 0 ? (
          <div className="ot-required-tests">
            <h3>Required Tests</h3>
            <div className="ot-required-tests-list">
              {requiredTests.map((test) => {
                const checked = values.completedRequiredTests.includes(test.id);
                return (
                  <div className="ot-required-test-row" key={test.id}>
                    <span>{test.name}</span>
                    <label className="ot-required-test-toggle">
                      <input
                        type="checkbox"
                        checked={checked}
                        aria-label={`Confirm ${test.name}`}
                        onChange={() => set('completedRequiredTests', checked
                          ? values.completedRequiredTests.filter((id) => id !== test.id)
                          : [...values.completedRequiredTests, test.id])}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
      <div className="ipd-daycare-form-actions"><button className="button button-primary" type="submit" disabled={mutation.isPending}>{mutation.isPending ? 'Saving...' : isFirstSchedule ? 'Edit Details' : 'Save Schedule'}</button></div>
    </form>
  );
};

export const OtDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const query = useOtCase(caseId);
  const statusMutation = useUpdateOtCaseStatus();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  // Report and billing links open a case straight on its bills with ?tab=bill-details.
  const [selectedTab, setSelectedTab] = useState<DetailTab | null>(() => (searchParams.get('tab') === 'bill-details' ? 'bill-details' : null));
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const billsQuery = useIpdBills(caseId);

  if (query.isLoading) return <DetailSkeleton label="Loading OT case" />;
  if (query.isError || !query.data) return <ErrorState title="OT case not found" message="The OT case could not be loaded." />;
  const row = query.data;
  const canSchedule = row.status === 'registered' || row.status === 'scheduled';
  // A registered case opens on the schedule form, since scheduling is the next step for it.
  const activeTab: DetailTab = selectedTab ?? (row.status === 'registered' ? 'schedule' : 'case-details');
  const address = [row.patientSnapshot.address, row.patientSnapshot.district, row.patientSnapshot.state, row.patientSnapshot.pinCode].filter(Boolean).join(', ') || '-';
  const timeSlot = row.startTime && row.endTime ? `${formatOtTime(row.startTime)} - ${formatOtTime(row.endTime)}` : '-';
  const bills = billsQuery.data ?? [];
  const effectiveBillId = selectedBillId ?? bills[0]?.id ?? null;
  const activeBills = bills.filter((bill) => bill.status !== 'cancelled');
  const totalDue = activeBills.reduce((sum, bill) => sum + bill.dueAmount, 0);
  const totalCredit = activeBills.reduce((sum, bill) => sum + bill.creditAmount, 0);
  const creditBill = activeBills.find((bill) => bill.id === effectiveBillId && bill.creditAmount > 0)
    ?? activeBills.find((bill) => bill.creditAmount > 0)
    ?? null;
  const lastBillNumber = bills[0]?.billNumber ?? null;
  const billableAdmission: BillableAdmission = {
    id: row.id,
    patient: row.patient,
    patientSnapshot: { name: row.patientSnapshot.name, uhid: row.patientSnapshot.uhid },
    bedCategory: row.bedCategory ?? { id: '', name: '-' },
    bed: row.bed ?? { id: '', name: '-' },
    admissionDate: row.registrationDate,
    status: billableStatus(row.status),
    admissionLabel: `OT No ${row.otNumber}`,
  };
  const viewBill = (billId: string) => { setSelectedBillId(billId); setSelectedTab('bill-details'); };
  const printBill = () => {
    if (!effectiveBillId) { showToast('Create a bill for this OT case before printing.', 'warning'); return; }
    openAuthenticatedRoute(`/ot/${row.id}/print-bill/${effectiveBillId}`);
  };

  const changeStatus = (status: OtCaseStatus) => void confirmUpdate(`the status of OT case ${row.otNumber}`).then((confirmed) => {
    if (!confirmed) return;
    statusMutation.mutate({ id: row.id, status }, {
      onSuccess: () => { setSelectedTab('case-details'); showToast('Status updated successfully.', 'success'); },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The case status could not be updated.', 'error'),
    });
  });
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) void navigate(-1);
    else void navigate('/ot');
  };

  return (
    <div className="page-stack opd-page ipd-daycare-page opd-bill-page">
      <div className="page-heading opd-print-hidden">
        <div><p className="eyebrow">OT</p><h1>OT Case Details</h1></div>
        <div className="opd-heading-actions">
          <button className="button button-secondary" type="button" onClick={goBack}>Back</button>
        </div>
      </div>

      <div className="opd-bill-layout">
        <aside className="content-card opd-bill-sidebar">
          <div className="opd-bill-patient-box">
            <p><strong>Patient Name : </strong>{row.patientSnapshot.name}</p>
            <p><strong>UHID : </strong>{row.patientSnapshot.uhid ?? '-'}</p>
            <p><strong>Sex : </strong>{row.patientSnapshot.gender} || <strong>Age : </strong>{row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</p>
            <p><strong>Mobile : </strong>{row.patientSnapshot.phone ?? '-'}</p>
            <p><strong>Address : </strong>{address}</p>
            <p><strong>Under Doctor : </strong>{doctorNameOrDash(row.underDoctor?.name)}</p>
            <p><strong>Department : </strong>{row.underDoctor?.department ?? '-'}</p>
            <p><strong>Bed : </strong>{row.bed ? `${row.bed.name} (${row.bedCategory?.name ?? '-'})` : '-'}</p>
            <p><strong>OT Room : </strong>{row.otRoom?.name ?? '-'}</p>
            <p><strong>Procedure : </strong>{row.procedureType?.name ?? '-'}</p>
            <p><strong>OT Date : </strong>{row.scheduledDate ? `${formatOtDate(row.scheduledDate)} ${timeSlot}` : 'Not scheduled'}</p>
            <p><strong>OT Status : </strong>{statusLabels[row.status]}</p>
            <p><strong>Last Bill No : </strong>{lastBillNumber ?? '-'}</p>
            {totalCredit > 0 ? <p><strong>Credit Amount : </strong><span className="opd-bill-patient-credit">{currency(totalCredit)}</span></p> : null}
            {totalDue > 0 ? <p><strong>Due : </strong><span className="opd-bill-patient-due">{currency(totalDue)}</span></p> : null}
          </div>
          <BillActionGrid>
            <BillActionTile icon={IconIdCard} label="Patient Profile" to={`/patients/${row.patient.id}`} />
            <BillActionTile icon={IconBillCheck} label="Case Details" active={activeTab === 'case-details'} onClick={() => setSelectedTab('case-details')} />
            {canSchedule ? <BillActionTile icon={IconCalendar} label="Edit Details" active={activeTab === 'schedule'} onClick={() => setSelectedTab('schedule')} /> : null}
            {billTiles.map((tab) => tab.key === 'refund' && (totalCredit <= 0 || row.status === 'cancelled') ? null : (
              <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} onClick={() => setSelectedTab(tab.key)} />
            ))}
            <BillActionTile icon={IconPrinter} label="Print Bill" onClick={printBill} />
            {statusActions[row.status].map((action) => (
              <BillActionTile key={action.status} icon={action.icon} label={action.label} onClick={() => { if (!statusMutation.isPending) changeStatus(action.status); }} />
            ))}
          </BillActionGrid>
        </aside>

        <section className="content-card opd-bill">
          {activeTab === 'schedule' && canSchedule ? (
            <OtScheduleForm key={`${row.id}-${row.status}`} row={row} onSaved={() => setSelectedTab('case-details')} />
          ) : activeTab === 'bill-details' ? (
            <IpdBillDetailsView admission={billableAdmission} selectedBillId={effectiveBillId} onCreateBill={() => setSelectedTab('create-bill')} showRefundPanel={false} />
          ) : activeTab === 'create-bill' ? (
            <OtCreateBillForm admission={row} onCreated={viewBill} />
          ) : activeTab === 'edit-bill' ? (
            effectiveBillId ? (
              <OtEditBillForm admission={row} billId={effectiveBillId} onCancelled={() => setSelectedTab('bill-details')} />
            ) : (
              <EmptyState title="No bills yet" message="Create a bill for this OT case before editing one." action={{ label: 'Create New Bill', onClick: () => setSelectedTab('create-bill') }} />
            )
          ) : activeTab === 'all-bill' ? (
            <IpdAllBillList admission={billableAdmission} onViewBill={viewBill} />
          ) : activeTab === 'all-receipt' ? (
            <IpdAllReceiptList admission={billableAdmission} printPath={`/ot/${row.id}/print-receipt`} />
          ) : activeTab === 'all-refund' ? (
            <IpdAllRefundList admission={billableAdmission} />
          ) : activeTab === 'refund' ? (
            creditBill ? (
              <OtRefundPanel patientName={row.patientSnapshot.name} bill={creditBill} bills={bills} />
            ) : (
              <EmptyState title="No credit available" message="A refund can be recorded only when an OT bill has an overpayment credit." />
            )
          ) : activeTab === 'new-receipt' ? (
            <IpdNewReceiptForm admission={billableAdmission} onRecorded={viewBill} />
          ) : (
          <>
          <header className="opd-bill-banner">
            <div className="opd-bill-banner-row">
              <div className="opd-bill-banner-fields">
                <span>OT No : {row.otNumber}</span>
                <span>Registration Date : {formatOtDateTime(row.registrationDate)}</span>
              </div>
              <span className={`ot-badge ${row.status}`}>{statusLabels[row.status]}</span>
            </div>
            <p>Registered By - {row.generatedBy.name}</p>
          </header>

          <section className="opd-bill-subsection">
            <h3>Patient Details</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Patient</dt><dd>{row.patientSnapshot.name}</dd></div>
              <div><dt>UHID</dt><dd>{row.patientSnapshot.uhid ?? '-'}</dd></div>
              <div><dt>Mobile</dt><dd>{row.patientSnapshot.phone ?? '-'}</dd></div>
              <div><dt>Gender / Age</dt><dd>{row.patientSnapshot.gender}, {row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</dd></div>
              <div><dt>Marital Status</dt><dd>{row.patientSnapshot.maritalStatus ?? '-'}</dd></div>
              <div><dt>Guardian Name</dt><dd>{row.patientSnapshot.guardianName ?? '-'}</dd></div>
              <div><dt>Relation</dt><dd>{row.patientSnapshot.relation ?? '-'}</dd></div>
              <div><dt>Guardian Contact No</dt><dd>{row.patientSnapshot.guardianContactNo ?? '-'}</dd></div>
              <div><dt>Aadhaar Card</dt><dd>{row.patientSnapshot.aadhaar ?? '-'}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{address}</dd></div>
            </dl>
          </section>

          <section className="opd-bill-subsection">
            <h3>Registration Information</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Registration Date</dt><dd>{formatOtDateTime(row.registrationDate)}</dd></div>
              <div><dt>Patient Type</dt><dd>{row.insuranceType ?? '-'}</dd></div>
              <div><dt>Insurance No.</dt><dd>{row.insuranceNumber ?? '-'}</dd></div>
              <div><dt>Under Doctor</dt><dd>{doctorNameOrDash(row.underDoctor?.name)}</dd></div>
              <div><dt>Department</dt><dd>{row.underDoctor?.department ?? '-'}</dd></div>
              <div><dt>Bed Category</dt><dd>{row.bedCategory?.name ?? '-'}</dd></div>
              <div><dt>Bed</dt><dd>{row.bed?.name ?? '-'}</dd></div>
              <div><dt>Referred By</dt><dd>{row.referredBy?.name ?? '-'}</dd></div>
              {row.diagnosis ? <div className="wide"><dt>Diagnosis</dt><dd dangerouslySetInnerHTML={{ __html: row.diagnosis }} /></div> : null}
              {row.notes ? <div className="wide"><dt>Notes</dt><dd dangerouslySetInnerHTML={{ __html: row.notes }} /></div> : null}
            </dl>
          </section>

          <section className="opd-bill-subsection">
            <h3>OT Schedule</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Procedure</dt><dd>{row.procedureType?.name ?? '-'}</dd></div>
              <div><dt>OT Room</dt><dd>{row.otRoom?.name ?? '-'}</dd></div>
              <div><dt>Surgeon</dt><dd>{doctorNameOrDash(row.surgeon?.name)}</dd></div>
              <div><dt>Anesthetist</dt><dd>{doctorNameOrDash(row.anesthetist?.name)}</dd></div>
              <div><dt>OT Date</dt><dd>{row.scheduledDate ? formatOtDate(row.scheduledDate) : 'Not scheduled'}</dd></div>
              <div><dt>Time Slot</dt><dd>{timeSlot}</dd></div>
            </dl>
          </section>

          {(row.procedureType?.requiredTests.length ?? 0) > 0 ? (
            <section className="opd-bill-subsection">
              <h3>Required Tests</h3>
              <div className="ot-required-tests-list ot-required-tests-results">
                {row.procedureType?.requiredTests.map((test) => {
                  const completed = row.completedRequiredTests.some((item) => item.id === test.id);
                  return (
                    <div className="ot-required-test-row" key={test.id}>
                      <span>{test.name}</span>
                      <span className={`ot-required-test-result ${completed ? 'done' : 'pending'}`}>
                        {completed ? 'Done' : 'Pending'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="opd-bill-subsection">
            <h3>Responsible Person Details</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Name</dt><dd>{row.responsiblePerson.name ?? '-'}</dd></div>
              <div><dt>Relation With Patient</dt><dd>{row.responsiblePerson.relation ?? '-'}</dd></div>
              <div><dt>Phone No</dt><dd>{row.responsiblePerson.phone ?? '-'}</dd></div>
              <div><dt>Age</dt><dd>{row.responsiblePerson.age ?? '-'}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{row.responsiblePerson.address ?? '-'}</dd></div>
            </dl>
          </section>
          </>
          )}
        </section>
      </div>
    </div>
  );
};
